import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

import { useRole } from './useRole';

interface PermissionsContextValue {
	permissions: string[];
	isLoading: boolean;
}

interface PermissionsProviderProps {
	children: ReactNode;
}

interface PermissionRoles {
	isUser: boolean;
	isAdmin: boolean;
	isStaff: boolean;
	isContractor: boolean;
}

// local perms. They are not on auth0
const localPerms: Record<string, (roles: PermissionRoles) => boolean> = {
	'req:note:read': ({ isStaff, isContractor }) => isStaff || isContractor,
};

const PermissionsContext = createContext<PermissionsContextValue | null>(null);

const decodePermissions = (token: string): string[] => {
	const encodedPayload = token.split('.')[1];
	if (!encodedPayload) {
		return [];
	}

	const base64 = encodedPayload.replace(/-/g, '+').replace(/_/g, '/');
	const paddedBase64 = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
	const payload = JSON.parse(atob(paddedBase64));

	return Array.isArray(payload.permissions)
		? payload.permissions.filter((permission: unknown) => typeof permission === 'string')
		: [];
};

export const PermissionsProvider = ({ children }: PermissionsProviderProps) => {
	const { getAccessTokenSilently, isAuthenticated } = useAuth0();
	const [permissions, setPermissions] = useState<string[]>([]);
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		if (!isAuthenticated) {
			setPermissions([]);
			setIsLoading(false);
			return;
		}

		let isCurrent = true;

		const loadPermissions = async () => {
			setIsLoading(true);
			try {
				const token = await getAccessTokenSilently();

				if (isCurrent) {
					setPermissions(decodePermissions(token));
				}
			} catch {
				if (isCurrent) {
					setPermissions([]);
				}
			} finally {
				if (isCurrent) {
					setIsLoading(false);
				}
			}
		};

		loadPermissions();

		return () => {
			isCurrent = false;
		};
	}, [getAccessTokenSilently, isAuthenticated]);

	const value = useMemo(() => ({ permissions, isLoading }), [permissions, isLoading]);

	return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
};

export const usePermissions = () => {
	const context = useContext(PermissionsContext);
	if (!context) {
		throw new Error('usePermissions must be used within a PermissionsProvider');
	}

	const { isAdmin, isUser, isStaff, isContractor } = useRole();
	const { permissions, isLoading } = context;

	const can = useCallback(
		(permission: string) =>
			!isLoading &&
			(isAdmin ||
				permissions.includes(permission) ||
				localPerms[permission]?.({ isUser, isAdmin, isStaff, isContractor }) === true),
		[isLoading, isAdmin, isUser, isStaff, isContractor, permissions],
	);

	return { ...context, can };
};
